import {
  FILTER_DAYS_MAP,
  GRAPH_LAYOUT,
  GRID_TIME_CONFIG,
  MASTERY_LEVEL_GRAPH_MAPPER
} from 'constants/studentGoals';

import {
  GENERAL_CONCEPTUAL_UNDERSTANDING,
  SIGNIFICANT_ERRORS_IN_WORK,
  WORK_IS_COMPLETE_AND_CORRECT
} from 'pages/Teachers/Insights/constants';
import { convertDaysToMilliseconds } from 'utils/date';

export const getStatusColor = ({ progress, targetAccuracy }) => {
  if (progress < targetAccuracy / 2)
    return MASTERY_LEVEL_GRAPH_MAPPER[SIGNIFICANT_ERRORS_IN_WORK]?.color;
  if (progress < targetAccuracy)
    return MASTERY_LEVEL_GRAPH_MAPPER[GENERAL_CONCEPTUAL_UNDERSTANDING]?.color;
  return MASTERY_LEVEL_GRAPH_MAPPER[WORK_IS_COMPLETE_AND_CORRECT]?.color;
};

export const getProgressColorClass = ({ color, styleModule }) => {
  const colorClasses = {
    green: styleModule.fillGreen,
    yellow: styleModule.fillYellow,
    pink: styleModule.fillPink
  };

  return colorClasses[color] || styleModule.fillGreen;
};

export const getStrokeColor = ({ masteryLevel }) => {
  const fallbackColor =
    MASTERY_LEVEL_GRAPH_MAPPER[SIGNIFICANT_ERRORS_IN_WORK].strokeColor;

  if (typeof masteryLevel === 'number') {
    const config = Object.values(MASTERY_LEVEL_GRAPH_MAPPER).find(
      (c) => c.graphicYValue === masteryLevel
    );
    return config?.strokeColor || fallbackColor;
  }

  return MASTERY_LEVEL_GRAPH_MAPPER[masteryLevel]?.strokeColor || fallbackColor;
};

export const getDotColorClass = ({ masteryLevel, styleModule }) => {
  const config =
    MASTERY_LEVEL_GRAPH_MAPPER[masteryLevel] ||
    MASTERY_LEVEL_GRAPH_MAPPER[SIGNIFICANT_ERRORS_IN_WORK];
  return styleModule[config.dotStyle] || styleModule.dotPink;
};

export const formatDateString = ({ isoString }) => {
  if (!isoString || typeof isoString !== 'string') {
    return '';
  }
  const dateParts = isoString.split('T')[0].split('-');
  if (dateParts.length !== 3 || dateParts.some((part) => isNaN(Number(part)))) {
    return isoString;
  }
  const [year, month, day] = dateParts;
  return `${day}/${month}/${year}`;
};

export const getMinTimeFromPeriod = ({ activePeriod }) => {
  const daysPeriod = FILTER_DAYS_MAP[activePeriod];
  return new Date().getTime() - convertDaysToMilliseconds({ days: daysPeriod });
};

export const calculateGraphPoints = ({
  records,
  styleModule,
  minTime,
  maxTime
}) => {
  const rawRecords = Array.isArray(records)
    ? records
    : records?.records || records?.data || [];

  if (rawRecords.length === 0) return [];

  const sortedRecords = [...rawRecords]
    .filter((record) => {
      const recordTime = new Date(record.created_at).getTime();
      return recordTime >= minTime && recordTime <= maxTime;
    })
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  if (sortedRecords.length === 0) return [];

  const timeSpan = maxTime - minTime;

  let points = sortedRecords.map((record) => {
    const currentTime = new Date(record.created_at).getTime();
    const timeRatio = timeSpan > 0 ? (currentTime - minTime) / timeSpan : 0.5;

    const masteryKey = record?.mastery_level;

    const config =
      MASTERY_LEVEL_GRAPH_MAPPER[masteryKey] ||
      MASTERY_LEVEL_GRAPH_MAPPER[SIGNIFICANT_ERRORS_IN_WORK];

    return {
      x: Number(
        (
          GRAPH_LAYOUT.PADDING_LEFT +
          timeRatio * GRAPH_LAYOUT.AVAILABLE_WIDTH
        ).toFixed(1)
      ),
      y: config.graphicYValue,
      masteryLevel: masteryKey,
      color: getDotColorClass({ masteryLevel: masteryKey, styleModule }),
      dateLabel: formatDateString({ isoString: record?.created_at }),
      statusLabel: config.statusLabel
    };
  });

  const minHorizontalGap = Math.min(
    GRAPH_LAYOUT.FALLBACK_GAP,
    GRAPH_LAYOUT.AVAILABLE_WIDTH / points.length
  );

  for (let i = 1; i < points.length; i++) {
    if (points[i].x < points[i - 1].x + minHorizontalGap) {
      points[i].x = Number((points[i - 1].x + minHorizontalGap).toFixed(1));
    }
  }

  if (points[points.length - 1].x > GRAPH_LAYOUT.MAX_X) {
    points[points.length - 1].x = GRAPH_LAYOUT.MAX_X;
    for (let i = points.length - 2; i >= 0; i--) {
      if (points[i].x > points[i + 1].x - minHorizontalGap) {
        points[i].x = Number((points[i + 1].x - minHorizontalGap).toFixed(1));
      }
    }
  }

  if (points[0].x < GRAPH_LAYOUT.PADDING_LEFT) {
    points[0].x = GRAPH_LAYOUT.PADDING_LEFT;
    for (let i = 1; i < points.length; i++) {
      if (points[i].x < points[i - 1].x + minHorizontalGap) {
        points[i].x = Number((points[i - 1].x + minHorizontalGap).toFixed(1));
      }
    }
  }

  return points;
};

export const computeSvgLines = ({ graphPoints }) => {
  const safePoints = Array.isArray(graphPoints) ? graphPoints : [];

  return safePoints.slice(1).map((point, index) => {
    const previousPoint = safePoints[index];
    const strokeColor = getStrokeColor({
      masteryLevel: point.masteryLevel || point.y
    });

    return {
      id: `line-${index}`,
      x1: previousPoint.x,
      y1: Number((GRAPH_LAYOUT.SVG_HEIGHT - previousPoint.y).toFixed(1)),
      x2: point.x,
      y2: Number((GRAPH_LAYOUT.SVG_HEIGHT - point.y).toFixed(1)),
      stroke: strokeColor
    };
  });
};

export const calculateGridTicks = ({ minTime, maxTime }) => {
  const ticks = [];
  const timeSpan = maxTime - minTime;
  const totalDays = timeSpan / GRID_TIME_CONFIG.MS_PER_DAY;

  let stepInDays = GRID_TIME_CONFIG.INTERVAL_SHORT_DAYS;
  if (totalDays > GRID_TIME_CONFIG.LONG_TERM_THRESHOLD_DAYS) {
    stepInDays = GRID_TIME_CONFIG.INTERVAL_LONG_DAYS;
  } else if (totalDays > GRID_TIME_CONFIG.MID_TERM_THRESHOLD_DAYS) {
    stepInDays = GRID_TIME_CONFIG.INTERVAL_MID_DAYS;
  }

  const stepInMs = convertDaysToMilliseconds({ days: stepInDays });

  for (let time = minTime; time <= maxTime; time += stepInMs) {
    const timeRatio = timeSpan > 0 ? (time - minTime) / timeSpan : 0.5;

    ticks.push({
      x: GRAPH_LAYOUT.PADDING_LEFT + timeRatio * GRAPH_LAYOUT.AVAILABLE_WIDTH,
      dateLabel: formatDateString({ isoString: new Date(time).toISOString() })
    });
  }
  return ticks;
};
